import './TimeProgressBar.css';

/**
 * Vendored replacement for the abandoned `time-progress-bar@1.0.1` package.
 *
 * The original relied on `Component.defaultProps` on function components, which
 * React 19 removed — that left `props` undefined and crashed reading
 * `props.className` (white screen on /projects). This port is behaviour-identical
 * but uses default parameters, so it is React 19-safe and needs no `props={{}}`
 * workaround at call sites.
 */

const HOUR_BAR_LABELS = {
  primary: ['12 pm'],
  secondary: ['6 am', '12 pm', '6 pm'],
  tertiary: ['4am', '8 am', '12 pm', '4 pm', '8 pm'],
};

const classNames = (...values) => values.filter(Boolean).join(' ');

const normalizeTime = value =>
  value && typeof value !== 'boolean' ? value : '00:00:00';

const timeToSeconds = time =>
  time
    .split(':')
    .map(Number)
    .reduce((acc, part) => acc * 60 + part);

const secondsToPercent = seconds => (seconds / 86400) * 100;

function HourBar({ hourBar }) {
  const labels = HOUR_BAR_LABELS[hourBar];
  if (!labels) return hourBar || '';
  return (
    <>
      {labels.map((label, index) => (
        <span key={`${label}-${index}`} className="time-item">
          {label}
        </span>
      ))}
    </>
  );
}

function Bar({
  barLength,
  barLeftPosition,
  color = '#20CF7E',
  children,
  className,
  style = {},
  props = {},
}) {
  return (
    <div
      {...props}
      style={{
        ...style,
        width: `${barLength}%`,
        left: `${barLeftPosition}%`,
        backgroundColor: color,
      }}
      className={classNames('timeProgressBar-timebar', className)}>
      {!!children && <div className="timebar-box">{children}</div>}
    </div>
  );
}

export function TimeProgressBar({
  className,
  style,
  data = [],
  backgroundColor = '#F5F6F8',
  hourBar = 'secondary',
  props = {},
}) {
  return (
    <div className={classNames('timeProgressBar', className)} style={style}>
      <div
        {...props}
        className={classNames('timeProgressBar-root', props.className)}
        style={{ ...props.style, backgroundColor }}>
        {data.map(({ start, end, key, ...rest }, index) => {
          const barLeftPosition = secondsToPercent(
            timeToSeconds(normalizeTime(start)),
          );
          const endSeconds = timeToSeconds(normalizeTime(end));
          const barLength = end
            ? secondsToPercent(endSeconds) - barLeftPosition
            : endSeconds + barLeftPosition;
          return (
            <Bar
              key={key || `${index}time_bar`}
              barLeftPosition={barLeftPosition}
              barLength={barLength}
              {...rest}
            />
          );
        })}
      </div>
      <div className="timeProgressBar-timesHolder">
        <HourBar hourBar={hourBar} />
      </div>
    </div>
  );
}

export default TimeProgressBar;
